import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";

const STATUS_LABELS = { draft: "Brouillon", announced: "Annoncé", closed: "Clôturé" };
const STATUS_CLS = { draft: "bg-slate-100 text-slate-500", announced: "bg-sage/20 text-navy", closed: "bg-red-100 text-red-600" };
const EMPTY = { performance_year: 2026, travel_year: 2027, destination: "", start_date: "", end_date: "", max_participants: "", status: "draft" };

export const TravelEditions = () => {
  const [editions, setEditions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(EMPTY);
  const [showNew, setShowNew] = useState(false);
  const [saving, setSaving] = useState(false);

  const fetchEditions = useCallback(() => {
    api.get("/phase3/travel-editions")
      .then((res) => setEditions(res.data))
      .catch(() => toast.error("Impossible de charger les éditions"))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchEditions(); }, [fetchEditions]);

  const create = async () => {
    if (!form.performance_year || !form.travel_year) { toast.error("Années requises"); return; }
    setSaving(true);
    try {
      await api.post("/phase3/admin/travel-editions", {
        ...form, max_participants: form.max_participants ? Number(form.max_participants) : null,
        start_date: form.start_date || null, end_date: form.end_date || null,
      });
      toast.success("Édition créée");
      setShowNew(false); setForm(EMPTY); fetchEditions();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const updateField = async (edition, field, value) => {
    try {
      await api.patch(`/phase3/admin/travel-editions/${edition.id}`, { [field]: value });
      toast.success("Édition mise à jour");
      fetchEditions();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  if (loading) return <div className="flex justify-center py-16"><Loader2 className="h-7 w-7 animate-spin text-navy" /></div>;

  return (
    <div data-testid="travel-editions">
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">Voyage annuel CozyHome — une édition par année de performance (résultats vérifiés en janvier/février N+1).</p>
        <button data-testid="travel-new-edition" onClick={() => setShowNew((v) => !v)}
          className="flex items-center gap-1.5 rounded-full bg-navy px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-cognac">
          <Plus className="h-3.5 w-3.5" /> Nouvelle édition
        </button>
      </div>

      {showNew && (
        <div data-testid="travel-new-form" className="mt-4 grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-3">
          <input data-testid="travel-new-performance-year" type="number" placeholder="Année de performance" value={form.performance_year}
            onChange={(e) => setForm({ ...form, performance_year: Number(e.target.value) })}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="travel-new-travel-year" type="number" placeholder="Année du voyage" value={form.travel_year}
            onChange={(e) => setForm({ ...form, travel_year: Number(e.target.value) })}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="travel-new-max" type="number" placeholder="Participants max" value={form.max_participants}
            onChange={(e) => setForm({ ...form, max_participants: e.target.value })}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="travel-new-destination" placeholder="Destination" value={form.destination}
            onChange={(e) => setForm({ ...form, destination: e.target.value })}
            className="col-span-full rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="travel-new-start" type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <input data-testid="travel-new-end" type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })}
            className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm focus:border-cognac focus:outline-none" />
          <button data-testid="travel-new-submit" disabled={saving} onClick={create}
            className="col-span-full flex items-center justify-center gap-2 rounded-full bg-navy px-5 py-2.5 text-sm font-bold text-white transition-colors hover:bg-cognac disabled:opacity-50">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Créer l'édition
          </button>
        </div>
      )}

      <div className="mt-5 space-y-3">
        {editions.length === 0 ? (
          <p className="py-10 text-center text-sm text-slate-400">Aucune édition créée pour le moment.</p>
        ) : editions.map((e) => (
          <div key={e.id} data-testid={`travel-edition-${e.id}`} className="rounded-2xl border border-slate-200 bg-white p-5">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <p className="font-display text-lg font-medium text-navy">Voyage {e.travel_year} <span className="text-sm text-slate-400">(performance {e.performance_year})</span></p>
                <p className="mt-1 text-sm text-slate-500">{e.destination || "Destination non annoncée"}{e.max_participants ? ` — ${e.max_participants} participants max` : ""}</p>
              </div>
              <select data-testid={`travel-status-${e.id}`} value={e.status} onChange={(ev) => updateField(e, "status", ev.target.value)}
                className={`rounded-full border-0 px-3 py-1.5 text-xs font-bold focus:outline-none ${STATUS_CLS[e.status]}`}>
                {Object.entries(STATUS_LABELS).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
            <div className="mt-3 grid gap-3 sm:grid-cols-2">
              <input data-testid={`travel-destination-${e.id}`} defaultValue={e.destination} onBlur={(ev) => updateField(e, "destination", ev.target.value)}
                placeholder="Destination" className="rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-cognac focus:outline-none" />
              <input data-testid={`travel-max-${e.id}`} type="number" defaultValue={e.max_participants || ""} onBlur={(ev) => updateField(e, "max_participants", ev.target.value ? Number(ev.target.value) : null)}
                placeholder="Participants max" className="rounded-xl border border-slate-200 px-4 py-2 text-sm focus:border-cognac focus:outline-none" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};
